import React, { FC, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardHeader, FormGroup, Input, Label } from 'reactstrap';
import { ProductModel } from '../../../../../shared/orders';
import {
  ActionButtonModal,
  ElementHeader,
  ElementSelector,
  LinkButton,
  MaterialIcon,
} from '../../../../components';
import { JSONForm } from '../../../../components/json-form/JSONForm';
import { useTranslation } from '../../../../translations';
import { displayName } from '../../../../utils';
import { AddonInfoBadges, LocalSearchProvider } from '../utils';

interface AddonInfoProps {
  readonly product: ProductModel;
  readonly onUpdate: () => void;
}

const enum AddonInfoMode {
  View,
  Edit,
}

export const AddonInfo: FC<AddonInfoProps> = ({ product, onUpdate }) => {
  const { ts } = useTranslation();
  const [mode, setMode] = useState(AddonInfoMode.View);
  const [selectedId, setSelectedId] = useState<number>();
  const searchProvider = useMemo(
    () => new LocalSearchProvider(async () => await api.getProducts()),
    [],
  );

  if (mode === AddonInfoMode.View) {
    return (
      <Card className="mt-10" id="productAddons">
        <CardHeader>
          <div className="card-sub-header">{ts('addons')}</div>
          {!product.deletedAt && (
            <div className="float-end">
              <Button
                color="success"
                id="add"
                onClick={() => {
                  setMode(AddonInfoMode.Edit);
                }}
                title={ts('common.add')}
              >
                <MaterialIcon name="add" />
              </Button>
            </div>
          )}
        </CardHeader>
        <CardBody>
          {product.addons.length === 0 && <i>{ts('there_are_no_addons_for_this_product')}</i>}
          {product.addons.length > 0 &&
            product.addons.map((addon) => (
              <div id={`addon${addon.id}`} key={addon.id}>
                <ElementHeader
                  icon={{ iconName: 'card_giftcard' }}
                  rightContent={
                    <>
                      <LinkButton color="primary" to={`/housekeeping/products/${addon.id}`}>
                        <MaterialIcon name="visibility" />
                      </LinkButton>{' '}
                      <ActionButtonModal
                        actionContent="common.delete"
                        buttonContent={<MaterialIcon name="delete" />}
                        className="action-delete"
                        color="danger"
                        id={`product-addon-delete-${addon.id}`}
                        onComplete={async () => {
                          await api.deleteProductAddon(product.id, addon.id);
                          onUpdate();
                        }}
                        outline
                        title="common.delete"
                      >
                        {ts('confirm_product_addon_delete')}
                      </ActionButtonModal>
                    </>
                  }
                  title={`${displayName(addon)} (ID: ${addon.id})`}
                >
                  <AddonInfoBadges addon={addon} />
                </ElementHeader>
              </div>
            ))}
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mt-10">
      <CardHeader>
        <div className="card-sub-header">{ts('addons')}</div>
        {!product.deletedAt && (
          <div className="float-end">
            <Button
              color="danger"
              id="cancel"
              onClick={() => {
                setMode(AddonInfoMode.View);
              }}
              title={ts('common.cancel')}
            >
              <MaterialIcon name="cancel" />
            </Button>
          </div>
        )}
      </CardHeader>
      <CardBody>
        <JSONForm
          id="newAddonForm"
          method="post"
          onSuccess={() => {
            onUpdate();
            setMode(AddonInfoMode.View);
          }}
          path={`/api/products/${product.id}/addons`}
        >
          <FormGroup>
            <Label for="product">{ts('common.product_search')}</Label>
            <ElementSelector<ProductModel>
              id="addonProductSelector"
              maxItems={1}
              searchProvider={searchProvider}
              singleSelectionIdChanged={setSelectedId}
            />
            <Input id="addonId" name="addonId" type="hidden" value={selectedId} />
          </FormGroup>
          <FormGroup style={{ marginLeft: '7px' }}>
            <FormGroup check className="mt-10">
              <Input id="isFree" name="isFree" type="checkbox" />
              <Label check for="isFree">
                {ts('addon_is_complimentary_and_should')}
              </Label>
            </FormGroup>
            <FormGroup check className="mt-10">
              <Input id="hidden" name="hidden" type="checkbox" />
              <Label for="hidden">{ts('product.addon.hidden')}</Label>
            </FormGroup>
          </FormGroup>
          <Button block color="primary" disabled={!selectedId} id="submitNewAddon">
            {ts('create_addon')}
          </Button>
        </JSONForm>
      </CardBody>
    </Card>
  );
};
